import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Creating the perfect rounded edge with D3 curves",
  "description": "",
  "published": "2018-07-31T00:00:00.000Z",
  "redirect_from": ["/blog/creating-perfect-rounded-edge-d3-curves/swizec/8543"],
  "categories": "Front End, Technical",
  "hero": "./img/d3-d3-shape-master-img-cardinal.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`A coaching client showed me this design and asked: `}<em parentName="p">{`Ok, how do I build this?`}</em></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/07/tree-with-rounded-edges.jpg",
        "alt": "Tree structure with rounded edges"
      }}></img></p>
    <p>{`Well hmm… it's a tree of some sort. Each icon is a node, and each line is an edge. Nodes can have multiple children. I think 🤔`}</p>
    <p>{`You can build this with D3. Calculate each node's position, then iterate and render. An image imported as an SVG component will do. Webpack can handle that for you.`}</p>
    <p>{`So you have node positions and you know which nodes are connected. Now what?`}</p>
    <p>{`You can turn that into pairs of coordinates. Point A to point B. A line between them.`}</p>
    <p>{`D3 is perfect for that! Drawing lines from A to B is super easy 👇`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// create a line path generator
const line = d3.line();
// pair of coordinates
const data = [
  [0, 0],
  [100, 100],
];

// draw, using JSX notation
<svg>
  <path d={line(data)} style={lineStyle}></path>
</svg>;
`}</code></pre>
    <p>{`That draws a line from point `}<inlineCode parentName="p">{`(0, 0)`}</inlineCode>{` to point `}<inlineCode parentName="p">{`(100, 100)`}</inlineCode>{`.`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/0yv3v8lljv?autoresize=1",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`But we don't want straight lines. Straight lines don't look good.`}</p>
    <p>{`Lucky for us, D3 has ample support for curves. Many different curve formulas to choose from.`}</p>
    <p>{`We can add some curve to our line with the `}<inlineCode parentName="p">{`.curve`}</inlineCode>{` method.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// create a line path generator
const line2 = d3.line().curve(d3.curveCardinal);
`}</code></pre>
    <p>{`If you try that, you'll see that nothing happens. Curves need multiple points in your line data to work well.`}</p>
    <p>{`Like this 👇`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/7zwm0zm8l6?autoresize=1",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`That's a nice curve and all, but not quite what we're looking for. And if you look at the `}<a parentName="p" {...{
        "href": "https://github.com/d3/d3-shape/blob/master/README.md#curves"
      }}>{`curve examples in D3 docs`}</a>{`, you'll see that nothing quite fits.`}</p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "888px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/70bdd083a3377ee01af1f73bbf99ffde/b04e4/d3-d3-shape-master-img-cardinal.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "26.905829596412556%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'108\\'%20viewBox=\\'0%200%20400%20108\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M113%2015l-4%201-3%201-8%205-11%207c-2%203-5%205-6%205l-2%202-10%2016-22%2034%2016-22%2019-27a126%20126%200%200127-18c2-1%2013-2%2016%200l4%206%203%204-2-5-2-5c0-2-6-6-9-6l-6%202m19%2016c0%203%2011%2029%2014%2034%206%208%206%208%2014%2011%2010%203%2028%202%2039-1l7-1c2%201%2019-17%2019-20l-8%208-11%209c-4%202-48%202-51%200-2-1-6-7-13-21l-10-19m110%204l-6%205c-4%203-11%2012-11%2014l8-8c5-4%209-8%2011-8a226%20226%200%200145%2017c1-1%200-3-2-3l-10-5c-12-7-21-11-26-12a212%20212%200%2001-9%200m96%2027c-6%202-12%205-10%206l6-2c3-1%207-2%208-1%202%200%2026%2024%2026%2026h2c1-1-26-30-28-30l-4%201\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/70bdd083a3377ee01af1f73bbf99ffde/ca0a1/d3-d3-shape-master-img-cardinal.webp 223w", "/static/70bdd083a3377ee01af1f73bbf99ffde/75680/d3-d3-shape-master-img-cardinal.webp 445w", "/static/70bdd083a3377ee01af1f73bbf99ffde/60055/d3-d3-shape-master-img-cardinal.webp 888w"],
              "sizes": "(max-width: 888px) 100vw, 888px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/70bdd083a3377ee01af1f73bbf99ffde/e92b6/d3-d3-shape-master-img-cardinal.png 223w", "/static/70bdd083a3377ee01af1f73bbf99ffde/e66bf/d3-d3-shape-master-img-cardinal.png 445w", "/static/70bdd083a3377ee01af1f73bbf99ffde/b04e4/d3-d3-shape-master-img-cardinal.png 888w"],
              "sizes": "(max-width: 888px) 100vw, 888px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/70bdd083a3377ee01af1f73bbf99ffde/b04e4/d3-d3-shape-master-img-cardinal.png",
              "alt": "d3 d3 shape master img cardinal",
              "title": "d3 d3 shape master img cardinal",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p><span parentName="p" {...{
        "className": "gatsby-resp-image-wrapper",
        "style": {
          "position": "relative",
          "display": "block",
          "marginLeft": "auto",
          "marginRight": "auto",
          "maxWidth": "888px",
          "textAlign": "center",
          "fontStyle": "italic"
        }
      }}>{`
      `}<a parentName="span" {...{
          "className": "gatsby-resp-image-link",
          "href": "/static/73f7473923b7fb563796a84f3419db21/b04e4/d3-d3-shape-master-img-catmullRom.png",
          "style": {
            "display": "block"
          },
          "target": "_blank",
          "rel": "noopener"
        }}>{`
    `}<span parentName="a" {...{
            "className": "gatsby-resp-image-background-image",
            "style": {
              "paddingBottom": "26.905829596412556%",
              "position": "relative",
              "bottom": "0",
              "left": "0",
              "backgroundImage": "url('data:image/svg+xml,%3csvg%20xmlns=\\'http://www.w3.org/2000/svg\\'%20width=\\'400\\'%20height=\\'108\\'%20viewBox=\\'0%200%20400%20108\\'%20preserveAspectRatio=\\'none\\'%3e%3cpath%20d=\\'M113%2015c-2%201-4%202-5%201l-1%201-8%204c-6%203-16%2011-15%2012l6-4c5-4%2016-10%2017-9l4-1c4-3%2010-2%2014%200%203%202%204%204%2010%2021l10%2024c3%204%203%203%201-2l-3-10-2-6c-1-1-5-14-4-16%200-2%200-2-1-1s-3-4-2-5h-1l-1-1-2-4c-8-6-12-7-17-4M79%2036l-5%207c-6%207-14%2019-22%2034l-8%2012v2a345%20345%200%200139-56c0-2-3-1-4%201m151%2010l-8%2011c-6%208-13%2014-15%2013l-1%201c0%201-8%204-14%205-14%202-36-1-37-4l-2-2-3-2c-3-2-2%200%200%203%206%207%2034%209%2050%204l6-1c2%201%2012-9%2017-16a275%20275%200%200111-16l-4%204m55%207l4%203%2011%207a95%2095%200%200014%2010c1%201%208-1%2014-5%207-4%2012-5%2013-4l2%201c2-1%2010%206%2017%2014%203%205%207%208%207%208%201-2-12-17-20-22-6-5-7-4-20%202l-10%204h-3c-1%200-14-8-24-17l-4-2-3-1%202%202\\'%20fill=\\'%23d3d3d3\\'%20fill-rule=\\'evenodd\\'/%3e%3c/svg%3e')",
              "backgroundSize": "cover",
              "display": "block"
            }
          }}></span>{`
  `}<picture parentName="a">{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/73f7473923b7fb563796a84f3419db21/ca0a1/d3-d3-shape-master-img-catmullRom.webp 223w", "/static/73f7473923b7fb563796a84f3419db21/75680/d3-d3-shape-master-img-catmullRom.webp 445w", "/static/73f7473923b7fb563796a84f3419db21/60055/d3-d3-shape-master-img-catmullRom.webp 888w"],
              "sizes": "(max-width: 888px) 100vw, 888px",
              "type": "image/webp"
            }}></source>{`
          `}<source parentName="picture" {...{
              "srcSet": ["/static/73f7473923b7fb563796a84f3419db21/e92b6/d3-d3-shape-master-img-catmullRom.png 223w", "/static/73f7473923b7fb563796a84f3419db21/e66bf/d3-d3-shape-master-img-catmullRom.png 445w", "/static/73f7473923b7fb563796a84f3419db21/b04e4/d3-d3-shape-master-img-catmullRom.png 888w"],
              "sizes": "(max-width: 888px) 100vw, 888px",
              "type": "image/png"
            }}></source>{`
          `}<img parentName="picture" {...{
              "className": "gatsby-resp-image-image",
              "src": "/static/73f7473923b7fb563796a84f3419db21/b04e4/d3-d3-shape-master-img-catmullRom.png",
              "alt": "d3 d3 shape master img catmullRom",
              "title": "d3 d3 shape master img catmullRom",
              "loading": "lazy",
              "style": {
                "width": "100%",
                "height": "100%",
                "margin": "0",
                "verticalAlign": "middle",
                "position": "absolute",
                "top": "0",
                "left": "0"
              }
            }}></img>{`
        `}</picture>{`
  `}</a>{`
    `}</span></p>
    <p>{`After some experimentation, I found a solution.`}</p>
    <lite-youtube {...{
      "videoid": "VyCP-3gL72k",
      "videostartat": "0"
    }}></lite-youtube>
    <p>{`👉 a React component that takes 2 points, injects 2 juuuust perfectly spaced points, and draws a D3 curve between them.`}</p>
    <iframe {...{
      "src": "https://codesandbox.io/embed/4zvl8njr0x?autoresize=1",
      "style": {
        "width": "100%",
        "height": "500px",
        "border": "0",
        "borderRadius": "4px",
        "overflow": "hidden"
      },
      "allow": "accelerometer; ambient-light-sensor; camera; encrypted-media; geolocation; gyroscope; hid; microphone; midi; payment; usb; vr; xr-spatial-tracking",
      "sandbox": "allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts"
    }}></iframe>
    <p>{`The poorly named `}<inlineCode parentName="p">{`<RoundedCorner>`}</inlineCode>{` component is just 15 lines of Prettier'd code. All values discovered experimentally.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const RoundedCorner = ({ start, end, radius = 5 }) => {
  const line = d3
    .line()
    .x(d => d[0])
    .y(d => d[1])
    .curve(d3.curveBundle.beta(1));

  const points = [
    start,
    [start[0], end[1] - radius],
    [start[0] + radius, end[1]],
    end
  ];

  return <path d={line(points)} style={lineStyle}>;
};
</path>
`}</code></pre>
    <p>{`We take `}<inlineCode parentName="p">{`start`}</inlineCode>{` and `}<inlineCode parentName="p">{`end`}</inlineCode>{` coordinates, and the desired `}<inlineCode parentName="p">{`radius`}</inlineCode>{`. Similar to CSS rounded borders.`}</p>
    <p>{`Experimentally, I discovered that for best results, you have to place 2 points between the two endpoints. One on each side of the rounded corner you want.`}</p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2018/07/sketch.jpg",
        "alt": null
      }}></img></p>
    <p>{`Then you have to use the `}<inlineCode parentName="p">{`curveBundle`}</inlineCode>{` generator with the `}<inlineCode parentName="p">{`beta`}</inlineCode>{` factor set to `}<inlineCode parentName="p">{`1`}</inlineCode>{`. I honestly don't know what that means but it works.`}</p>
    <p>{`You can see I tried a few different configurations in the example CodeSandbox. That's because some curves produced weird edges when turned around like that.`}</p>
    <p>{`But not good ol' curveBundle.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      